import { AccountCircle, Flag, InfoOutlined } from '@mui/icons-material';
import { Box } from '@mui/material';
import { t } from 'i18next';
import { isAqueductEnabled } from '../../auth/utils';
import { FeatureFlagsType, getFeatureFlag } from '../../config/launch-darkly/FeatureFlagsController';
import { Footer } from '../../layout/components/Footer';
import { NavBar } from '../../layout/components/NavBar';
import { ModalManager } from '../../modal-flows/services/ModalManager';
import { UserOrigin } from '../../organization/users/enums/UserOrigin';
import { EXTERNAL_URLS, MAIN_NAV_TABS, PATHS, Route } from '../../routing/routes';
import { AppConfigContext } from '../services/AppConfigContext';
import { AuthUserContext } from '../services/AuthUserContext';
import { UrlHelper } from '../utils/UrlHelper';
import { BaseController, BaseProps, BaseState } from './BaseController';

type Props = BaseProps & {};

type State = BaseState & {
  initialLoad: boolean;
  version: string | null;
  permissions: {
    canChangeOwnPassword: boolean;
    canReadOrganizationKeys: boolean;
    canReadOrganizationCertificates: boolean;
    canReadOrganizationUsers: boolean;
    canReadSensors: boolean;
    canReadScans: boolean;
  };
};

export class MainLayoutController extends BaseController<Props, State> {
  /**
   *
   * In some weird cases around Routing, MainLayoutController is being unmounted, but AbortController canceling
   * of requests is not taken properly.
   * That results in setting state after component has unmounted.
   * This happened only in tests, but indicates that there is something else that might be a problem.
   *
   * We can't replicate this scenario with manual testing.
   * This is meant to be used only in this contained environment of MainLayoutController.
   *
   * If a solution is found, feel free to remove this flag.
   */
  private _mounted: boolean = false;
  private readonly modalManager = new ModalManager();

  constructor(props: Props, context: unknown) {
    super(props, context);

    this.state = {
      ...super.getBaseState(),
      initialLoad: true,
      version: AuthUserContext.getVersion(),
      permissions: {
        canChangeOwnPassword: false,
        canReadOrganizationKeys: false,
        canReadOrganizationCertificates: false,
        canReadOrganizationUsers: false,
        canReadSensors: false,
        canReadScans: false,
      },
    };
  }

  componentDidMount() {
    super.componentDidMount();
    this._mounted = true;

    Promise.all([
      this.permissionsService.canChangePassword(),
      this.permissionsService.canAccessOrganizationKey(),
      this.permissionsService.canAccessOrganizationCertificate(),
      this.permissionsService.canAccessOrganizationUsers(),
      this.permissionsService.canDownloadTracers(),
      this.permissionsService.canAccessOrganizationScans(),
    ])
      .then(([canChangeOwnPassword, canReadOrganizationKeys, canReadOrganizationCertificates, canReadOrganizationUsers, canReadSensors, canReadScans]) => {
        if (!this._mounted) {
          return;
        }

        this.setState({
          initialLoad: false,
          permissions: {
            canChangeOwnPassword,
            canReadOrganizationKeys,
            canReadOrganizationCertificates,
            canReadOrganizationUsers,
            canReadSensors,
            canReadScans,
          },
        });
      })
      .catch((e: unknown) => {
        if (!this._mounted) {
          return;
        }

        this.handleError(e, undefined, true);
      });
  }

  componentWillUnmount() {
    super.componentWillUnmount();

    this._mounted = false;
  }

  getRoutes(): Route[] {
    const { canReadOrganizationKeys, canReadOrganizationCertificates, canReadOrganizationUsers, canReadSensors, canReadScans } = this.state.permissions;
    return MAIN_NAV_TABS({ canReadOrganizationKeys, canReadOrganizationCertificates, canReadOrganizationUsers, canReadSensors, canReadScans });
  }

  getButtonRoutes(): Route[] {
    const bugReportUrl = getFeatureFlag<string>(FeatureFlagsType.CONTROL_CENTER_BUG_REPORT_URL);

    const result: Route[] = [];
    if (bugReportUrl) {
      result.push({ name: t('reportBug'), Icon: Flag, path: bugReportUrl, openInNewTab: true });
    }
    result.push({
      name: t('a11y.navigation.docs'),
      path: UrlHelper.wrapExternalDocsUrl(EXTERNAL_URLS.DOCS_HOME),
      Icon: InfoOutlined,
      openInNewTab: true,
    });
    result.push({ name: t('account'), path: '', Icon: AccountCircle, children: this.getAccountRoutes() });

    return result;
  }

  private getAccountRoutes(): Route[] {
    const currentUser = AuthUserContext.getUser();

    const { permissions, version } = this.state;
    const useAqueduct = isAqueductEnabled();

    const routes: Route[] = [];
    routes.push({
      name: currentUser.getFormattedName(),
      path: permissions.canReadOrganizationUsers ? PATHS.USERS : '',
      divider: true,
    });

    routes.push({
      name: `${t('accountButtonChildren.changelog')} ${version ? `(${version})` : ''}`,
      path: UrlHelper.wrapExternalDocsUrl(EXTERNAL_URLS.DOCS_CHANGELOG),
      openInNewTab: true,
      divider: true,
    });
    const changePasswordRoute = this.getChangePasswordRoute();
    // Password management is not available when AQueduct is used
    if (changePasswordRoute && !useAqueduct) {
      routes.push(changePasswordRoute);
    }
    routes.push({
      name: t('accountButtonChildren.logout'),
      path: '',
      // Force a window navigation to `PATHS.LOGOUT_PAGE` to clear the server session
      onClick: () => window.location.assign(PATHS.LOGOUT_PAGE),
      'data-role': 'cc-logout-button',
    });
    return routes;
  }

  render() {
    const { initialLoad } = this.state;
    return this.renderBase(
      <>
        <NavBar routes={this.getRoutes()} buttonRoutes={this.getButtonRoutes()} />
        <Box component="main" id="cc-main" data-role={`cc-main-${initialLoad ? 'loading' : 'loaded'}`}>
          {this.props.children}
        </Box>
        <Footer />
      </>
    );
  }

  private getChangePasswordRoute(): Route | null {
    const currentUser = AuthUserContext.getUser();
    const webAccountConfig = AppConfigContext.getPresets().account;
    const isWebAccount = currentUser.getOrigin() === UserOrigin.WEB_ACCOUNT;
    const webAccountAvailable = isWebAccount && webAccountConfig !== null;
    const changePasswordLabel = t('accountButtonChildren.changePassword');
    const {
      permissions: { canChangeOwnPassword, canReadOrganizationUsers },
    } = this.state;

    if (canReadOrganizationUsers) {
      // Change password happens from the Users page and the user can see it: do not display the link
      return null;
    }
    if (canChangeOwnPassword) {
      // Local password change available, enable the modal
      return { name: changePasswordLabel, path: '', onClick: () => this.modalManager.changePassword(currentUser.getName()) };
    }
    if (webAccountAvailable) {
      // WebAccount password change available, external link
      return { name: changePasswordLabel, path: webAccountConfig.changePassword, openInNewTab: true };
    }
    // No password change mechanism available
    return null;
  }
}
